import type { Action, AnyAction, Reducer } from 'redux';

export interface NamedAction extends Action {
  domain: string;
}

const isNamedAction = (action: Action): action is NamedAction => {
  return 'domain' in action;
};

/**
 * Creates a namespace where the given reducer is limited to
 * @method createNamedWrapperReducer
 * @param  reducerFunction           Higher Order Reducer
 * @param  reducerName               The namespace where this reducer will act. If using multiple HOR in a regular reducer, all of them will have the same reducerName
 * @return                           A new "regular" reducer, which filter actions by their namespace
 */
function createNamedWrapperReducer(reducerFunction: Reducer<any, any>, reducerName: string) {
  return <S, A extends Action = Action>(state: S, action: A) => {
    const isInitializationCall = state === undefined;
    if (isInitializationCall) {
      return reducerFunction(state, action);
    }

    if (!isNamedAction(action)) {
      return state;
    }

    return action.domain === reducerName ? reducerFunction(state, action) : state;
  };
}

type GenericReducer = (...args: any) => any;

/**
 * Returns a reducer composed by the given reducers.
 * @method rootReducer
 * @param  reducers           A reducers list, including the Higher Order Reducers
 * @param  initialState       The main reducer initial state
 * @return                    A new reducer which runs everything from the given list
 */
function rootReducer<R extends GenericReducer, S>(reducers: R[], initialState: S): Reducer {
  return (state: S = initialState, action: AnyAction) => {
    let updatedState = state;

    reducers.forEach((reducer) => {
      updatedState = reducer(updatedState, action);
    });
    return updatedState;
  };
}

export { createNamedWrapperReducer, rootReducer };
